import React, {
  Children,
  cloneElement,
  isValidElement,
  useState,
  useEffect,
} from "react";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { TableNode, TableRowNode, TableCellNode } from "@lexical/table";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
  $insertNodes,
} from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";

import ToolBar from "../Editor/Toolbar";
import lexicalEditorTheme from "../../Themes/lexicalEditortheme";
import OnChangePlugin from "../../plugins/OnChangePlugin";
import { ImageNode } from "../../plugins/ImagePlugin/ImageNode";
import { ImagePlugin } from "../../plugins/ImagePlugin/ImagePlugin";
import DirectionPlugin from "../../plugins/DirectionPlugin";
import InitialStatePlugin from "../../plugins/InitialStatePlugin";
import PastePlugin from "../../plugins/PasterPlugin/PastePlugin";
import { HtmlNode } from "../../plugins/PasterPlugin/HtmlNode";
import { StyledTextNode } from "../../plugins/StyledTextNode";

import { ComposeBoxProps } from "./types";
import "./styles.css";
import "../Editor/EditorWrapper/editor.css";

// Helper component to handle AI Insertion logic inside the Lexical Context
const AiIntegrationPlugin = ({
  aiContent,
  onAiInsert,
  onAiDiscard,
  aiContentComponent,
  showAiPreview,
}: {
  aiContent?: string;
  onAiInsert?: (content: string) => void;
  onAiDiscard?: () => void;
  aiContentComponent?: React.ReactNode;
  showAiPreview?: boolean;
}) => {
  const [editor] = useLexicalComposerContext();

  const handleInsert = React.useCallback(() => {
    if (!aiContent) return;

    editor.update(() => {
      // Create a parser to convert the AI content string (HTML) to nodes
      // Or if it's plain text, just insert text nodes.
      // Assuming aiContent might be HTML or plain text.
      // For safety, let's try to handle as HTML if it looks like it, otherwise text.

      try {
        const parser = new DOMParser();
        const dom = parser.parseFromString(aiContent, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        // Ensure nodes have LTR direction
        nodes.forEach((node) => {
          if ("setDirection" in node) {
            (node as any).setDirection("ltr");
          }
        });
        $insertNodes(nodes);
      } catch (e) {
        // Fallback to plain text insertion
        const textNode = $createTextNode(aiContent);
        $insertNodes([textNode]);
      }
    });

    if (onAiInsert) {
      onAiInsert(aiContent);
    }
  }, [editor, aiContent, onAiInsert]);

  const handleDiscard = React.useCallback(() => {
    if (onAiDiscard) {
      onAiDiscard();
    }
  }, [onAiDiscard]);

  // Clone the AI component to inject handlers
  if (!showAiPreview || !aiContentComponent) return null;

  return (
    <div className="ai-preview-container">
      {Children.map(aiContentComponent, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child as React.ReactElement<any>, {
            onInsert: handleInsert,
            onDiscard: handleDiscard,
          });
        }
        return child;
      })}
    </div>
  );
};

export function ComposeBox({
  aiContentComponent,
  aiContent,
  showAiPreview,
  onAiInsert,
  onAiDiscard,
  placeholder = "",
  initialHTML = "",
  rightChildren,
  leftChildren,
  customTool,
  minHeight = "200px",
  onChange,
  editorChildren,
  isEditorVisible = true,
  showAttachFile = true,
}: ComposeBoxProps) {
  const [currentHTML, setCurrentHTML] = useState(initialHTML);

  const handleInternalChange = (html: string) => {
    setCurrentHTML(html);
    if (onChange) {
      onChange(html);
    }
  };

  const isAiPreviewActive = showAiPreview && aiContentComponent;

  const initialConfig = {
    namespace: "ComposeBox",
    theme: lexicalEditorTheme,
    nodes: [
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      TableNode,
      TableRowNode,
      TableCellNode,
      ImageNode,
      HtmlNode,
      StyledTextNode,
    ],
    onError: (error: Error) => {
      console.error(error);
    },
  };

  return (
    <div className="compose-box-wrapper" style={{ minHeight }}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className="compose-box-editor-container" style={{ flex: 1 }}>
          <div
            className="compose-box-scroll-area"
            style={{ flex: 1, overflowY: "auto", minHeight }}
          >
            {isAiPreviewActive &&
              currentHTML &&
              currentHTML !== '<p class="editor-paragraph"><br></p>' && (
                <div
                  className="compose-box-content-preview"
                  dangerouslySetInnerHTML={{ __html: currentHTML }}
                />
              )}

            {/* Render AI Component via Plugin to access Editor Context */}
            <AiIntegrationPlugin
              aiContent={aiContent}
              onAiInsert={onAiInsert}
              onAiDiscard={onAiDiscard}
              aiContentComponent={aiContentComponent}
              showAiPreview={showAiPreview}
            />

            <div
              style={{
                display: isAiPreviewActive ? "none" : "block",
              }}
            >
              {isEditorVisible ? (
                <>
                  <RichTextPlugin
                    contentEditable={
                      <ContentEditable
                        style={{ minHeight, padding: "16px" }}
                        className="compose-box-content-editable"
                        aria-placeholder={placeholder}
                        placeholder={null as any}
                      />
                    }
                    placeholder={
                      <div className="compose-box-placeholder">
                        {placeholder}
                      </div>
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                  <PastePlugin />
                  <ImagePlugin />
                </>
              ) : (
                editorChildren
              )}
            </div>
          </div>

          <ToolBar
            rightChildren={rightChildren}
            leftChildren={leftChildren}
            customTool={customTool}
            disabled={!!isAiPreviewActive}
            showAttachFile={showAttachFile}
          />
        </div>

        <HistoryPlugin />
        <InitialStatePlugin initialHTML={initialHTML} />
        <DirectionPlugin />
        <AutoFocusPlugin />
        <ListPlugin />
        <LinkPlugin />
        <ImagePlugin />
        <PastePlugin />
        <OnChangePlugin onChange={handleInternalChange} />
      </LexicalComposer>
    </div>
  );
}

export default ComposeBox;
